// The mobile composer HOME — clean and calm. Top to bottom: an undo/redo action row, the live
// score on its own row, the post preview (read-mostly; tapping a region drills into that region's
// focused editor + AI), and a glassy Ask-Taka bar that opens the whole-post chat. Depth is one tap
// away (the drill-downs), never in your face. The post preview is supplied by the parent (built
// from getPreview with interactive=false + an onSectionSelect that opens the drill-downs).

import type { ReactNode } from 'react'
import { Redo2, Sparkles, Undo2 } from 'lucide-react'
import { ScorePanel } from '../../components/process/ScorePanel'
import { useComposerEditor } from './CompositionEditorProvider'
import { MobileVersionControl } from './MobileVersionControl'

export function MobileComposerHome({
  creativeId,
  preview,
  onOpenChat,
}: {
  creativeId: string
  /** The platform preview, rendered read-mostly with tappable regions (built by the parent). */
  preview: ReactNode
  /** Opens the whole-post Ask-Taka chat. */
  onOpenChat: () => void
}) {
  const ctx = useComposerEditor()
  const editor = ctx?.editor

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-bg">
      {/* Row 1 — undo / redo. */}
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-line px-2 py-1.5">
        <button
          type="button"
          onClick={() => editor?.undo()}
          disabled={!editor?.canUndo}
          aria-label="Undo"
          className="flex h-10 w-10 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Undo2 size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor?.redo()}
          disabled={!editor?.canRedo}
          aria-label="Redo"
          className="flex h-10 w-10 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Redo2 size={18} />
        </button>
        {/* Version journey lives in a stable spot beside undo/redo. */}
        <MobileVersionControl />
      </div>

      {/* Row 2 (score) + the post preview, scrollable. Bottom padding lets the post scroll
          BEHIND the glass bar so it blurs through (the bar overlays this area). */}
      <div className="flex-1 overflow-y-auto px-3 py-3 pb-28">
        <ScorePanel creativeId={creativeId} className="mb-3" />
        <div className="flex justify-center">{preview}</div>
      </div>

      {/* Ask-Taka bar — overlays the scrolling content; the STRIP is glassy (post blurs through
          its margins) but the input PILL is a solid, clearly-bordered, readable input on top —
          high contrast, like production. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-line/60 bg-bg-2/70 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onOpenChat}
            data-ask-taka
            className="pointer-events-auto flex min-h-[46px] flex-1 items-center rounded-xl border border-line-bright bg-bg-3 px-4 text-left text-[14px] text-paper-dim"
          >
            Ask Taka to change anything…
          </button>
          <button
            type="button"
            onClick={onOpenChat}
            aria-label="Ask Taka"
            className="text-ink-on-acid pointer-events-auto flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-xl bg-acid shadow-[var(--shadow-acid-cta)]"
          >
            <Sparkles size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
